import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Input, Select, Space, Table, Tag, message } from "antd";
import moment from "moment";
import { Body } from "../../../components/main-body/body";
import ApiNotificationOutbox from "../../../api/notification-outbox";
import "./notification-outbox.css";

const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  "PENDING",
  "DISPATCHING",
  "SENT",
  "FAILED",
  "EXPIRED",
  "CANCELED",
].map((value) => ({ label: value, value }));

const STATUS_COLORS = {
  PENDING: "gold",
  DISPATCHING: "blue",
  SENT: "green",
  FAILED: "red",
  EXPIRED: "default",
  CANCELED: "default",
};

function getDefaultRange() {
  return [moment().subtract(24, "hours").toISOString(), moment().toISOString()];
}

function getPickerRange(range) {
  if (!range || !range[0] || !range[1]) {
    return null;
  }

  return [moment(range[0]), moment(range[1])];
}

function formatDate(value) {
  return value ? `${moment(value).utc().format("DD MMM YYYY, HH:mm:ss")}, UTC` : "-";
}

function stringify(value) {
  if (!value) return "-";

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
}

function maskToken(token) {
  if (!token) return "-";
  if (token.length <= 24) return token;

  return `${token.slice(0, 12)}...${token.slice(-10)}`;
}

const NotificationOutbox = () => {
  const [rows, setRows] = useState([]);
  const [health, setHealth] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
  });
  const [summary, setSummary] = useState({ statuses: [], triggers: [] });
  const [filters, setFilters] = useState({
    status: undefined,
    trigger: undefined,
    subjectId: "",
    projectId: "",
    range: getDefaultRange(),
  });
  const [loading, setLoading] = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);

  const handleRetry = async (id) => {
    try {
      setLoading(true);
      await ApiNotificationOutbox.retryNotificationOutbox(id);
      message.success("Notification re-enqueued for dispatch");
      loadData();
    } catch (error) {
      console.error("Retry failed:", error);
      message.error(error.response?.data?.error || "Failed to retry notification");
      setLoading(false);
    }
  };

  const loadData = useCallback(async (page = pagination.page, limit = pagination.limit) => {
    setLoading(true);

    try {
      const query = {
        page,
        limit,
        status: filters.status || undefined,
        trigger: filters.trigger || undefined,
        subjectId: filters.subjectId || undefined,
        projectId: filters.projectId || undefined,
      };

      if (filters.range && filters.range[0] && filters.range[1]) {
        query.dateFrom = filters.range[0];
        query.dateTo = filters.range[1];
      }

      const response = await ApiNotificationOutbox.getNotificationOutbox(query);
      setRows(response.data.notificationOutbox || []);
      setPagination(response.data.pagination || { page, limit, total: 0 });
      setSummary(response.data.summary || { statuses: [], triggers: [] });
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, pagination.page]);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);

    try {
      const response = await ApiNotificationOutbox.getNotificationOutboxHealth();
      setHealth(response.data);
    } catch (error) {
      console.error("Notification outbox health failed:", error);
      message.error(error.response?.data?.error || "Failed to load notification analytics");
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(1, pagination.limit);
    loadHealth();
  }, [filters, loadData, loadHealth, pagination.limit]);

  const triggerOptions = useMemo(() => (
    (summary.triggers || []).map((item) => ({
      label: `${item.trigger} (${item.count})`,
      value: item.trigger,
    }))
  ), [summary.triggers]);

  const statusCounts = useMemo(() => {
    const result = {};
    (summary.statuses || []).forEach((item) => {
      result[item.status] = Number(item.count || 0);
    });
    return result;
  }, [summary.statuses]);

  const healthStatusCounts = useMemo(() => {
    const result = {};
    (health?.db?.statuses || []).forEach((item) => {
      result[item.status] = Number(item.count || 0);
    });
    return result;
  }, [health]);

  const queueWaiting = Number(health?.queue?.waiting || 0);
  const overduePending = Number(health?.db?.overduePending || 0);
  const staleDispatching = Number(health?.db?.staleDispatching || 0);
  const queueStatus = queueWaiting > Number(health?.thresholds?.waitingWarn || 5000)
    ? "warning"
    : "ok";

  const columns = [
    {
      title: "Created",
      dataIndex: "createdAt",
      width: 180,
      render: formatDate,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (status) => <Tag color={STATUS_COLORS[status] || "default"}>{status}</Tag>,
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      width: 170,
      render: (value) => <span className="notification-outbox-code">{value}</span>,
    },
    {
      title: "Subject",
      dataIndex: "Subject",
      width: 220,
      render: (subject, row) => subject
        ? `${subject.displayId || subject.id} ${subject.email ? `- ${subject.email}` : ""}`
        : row.subjectId || "-",
    },
    {
      title: "Project",
      dataIndex: "Project",
      width: 180,
      render: (project, row) => project
        ? `${project.code || project.id} ${project.title ? `- ${project.title}` : ""}`
        : row.projectId || "-",
    },
    {
      title: "Attempts",
      dataIndex: "attempts",
      width: 90,
      render: (_, row) => `${row.attempts}/${row.maxAttempts}`,
    },
    {
      title: "Next attempt",
      dataIndex: "nextAttemptAt",
      width: 180,
      render: formatDate,
    },
    {
      title: "Sent",
      dataIndex: "sentAt",
      width: 180,
      render: formatDate,
    },
    {
      title: "FCM",
      dataIndex: "fcmMessageId",
      width: 220,
      render: (value, row) => value || row.fcmErrorCode || "-",
    },
    {
      title: "Token",
      dataIndex: "fcmToken",
      width: 180,
      render: (value) => <span title={value}>{maskToken(value)}</span>,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, row) => (
        <Button
          size="small"
          onClick={() => handleRetry(row.id)}
          disabled={loading}
        >
          Retry
        </Button>
      ),
    },
  ];

  return (
    <Body header={{ title: "Notification outbox" }} scroll>
      <div className="notification-outbox-page">
        <div className="notification-outbox-summary">
          {STATUS_OPTIONS.map((option) => (
            <div className="notification-outbox-summary-item" key={option.value}>
              <div className="notification-outbox-summary-label">{option.label}</div>
              <div className="notification-outbox-summary-value">
                {statusCounts[option.value] || 0}
              </div>
            </div>
          ))}
        </div>

        <div className="notification-outbox-filters">
          <Select
            allowClear
            placeholder="Status"
            value={filters.status}
            options={STATUS_OPTIONS}
            onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
          />
          <Select
            allowClear
            showSearch
            placeholder="Trigger"
            value={filters.trigger}
            options={triggerOptions}
            onChange={(value) => setFilters((prev) => ({ ...prev, trigger: value }))}
          />
          <Input
            allowClear
            placeholder="Subject ID"
            value={filters.subjectId}
            onChange={(event) => setFilters((prev) => ({ ...prev, subjectId: event.target.value }))}
          />
          <Input
            allowClear
            placeholder="Project ID"
            value={filters.projectId}
            onChange={(event) => setFilters((prev) => ({ ...prev, projectId: event.target.value }))}
          />
          <RangePicker
            value={getPickerRange(filters.range)}
            onChange={(value) => setFilters((prev) => ({
              ...prev,
              range: value && value[0] && value[1]
                ? [value[0].toISOString(), value[1].toISOString()]
                : null,
            }))}
          />
          <Space>
            <Button
              onClick={() => {
                loadData(1, pagination.limit);
                loadHealth();
              }}
            >
              Refresh
            </Button>
            <Button
              onClick={() => setFilters({
                status: undefined,
                trigger: undefined,
                subjectId: "",
                projectId: "",
                range: getDefaultRange(),
              })}
            >
              Reset
            </Button>
          </Space>
        </div>

        <div className="notification-outbox-health">
          <div className="notification-outbox-health-card">
            <div className="notification-outbox-health-header">
              <span>Queue</span>
              <Tag color={queueStatus === "warning" ? "red" : "green"}>
                {queueStatus === "warning" ? "Attention" : "OK"}
              </Tag>
            </div>
            <div className="notification-outbox-health-grid">
              {["waiting", "delayed", "active", "failed", "completed", "paused"].map((key) => (
                <div key={key}>
                  <div className="notification-outbox-summary-label">{key}</div>
                  <div className="notification-outbox-health-value">
                    {healthLoading ? "-" : Number(health?.queue?.[key] || 0)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="notification-outbox-health-card">
            <div className="notification-outbox-health-header">
              <span>Delivery analytics</span>
              <span className="notification-outbox-muted">
                {health?.generatedAt ? formatDate(health.generatedAt) : "-"}
              </span>
            </div>
            <div className="notification-outbox-health-grid">
              <div>
                <div className="notification-outbox-summary-label">overdue pending</div>
                <div className="notification-outbox-health-value">{healthLoading ? "-" : overduePending}</div>
              </div>
              <div>
                <div className="notification-outbox-summary-label">stale dispatching</div>
                <div className="notification-outbox-health-value">{healthLoading ? "-" : staleDispatching}</div>
              </div>
              <div>
                <div className="notification-outbox-summary-label">created 1h</div>
                <div className="notification-outbox-health-value">
                  {healthLoading ? "-" : Number(health?.db?.recentOneHour?.created || 0)}
                </div>
              </div>
              <div>
                <div className="notification-outbox-summary-label">sent 1h</div>
                <div className="notification-outbox-health-value">
                  {healthLoading ? "-" : Number(health?.db?.recentOneHour?.sent || 0)}
                </div>
              </div>
              <div>
                <div className="notification-outbox-summary-label">failed 1h</div>
                <div className="notification-outbox-health-value">
                  {healthLoading ? "-" : Number(health?.db?.recentOneHour?.failed || 0)}
                </div>
              </div>
              <div>
                <div className="notification-outbox-summary-label">pending total</div>
                <div className="notification-outbox-health-value">
                  {healthLoading ? "-" : Number(healthStatusCounts.PENDING || 0)}
                </div>
              </div>
            </div>
          </div>

          <div className="notification-outbox-health-card">
            <div className="notification-outbox-health-header">
              <span>Top FCM errors, 24h</span>
            </div>
            <div className="notification-outbox-errors">
              {(health?.db?.topErrorsLast24h || []).length === 0 && (
                <span className="notification-outbox-muted">No errors</span>
              )}
              {(health?.db?.topErrorsLast24h || []).map((item) => (
                <div className="notification-outbox-error-row" key={item.fcmErrorCode}>
                  <span className="notification-outbox-code">{item.fcmErrorCode}</span>
                  <strong>{item.count}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Table
          className="notification-outbox-table"
          columns={columns}
          dataSource={rows}
          expandable={{
            expandedRowRender: (row) => (
              <div className="notification-outbox-expanded">
                <div>
                  <h4>Payload</h4>
                  <pre>{stringify(row.payload)}</pre>
                </div>
                <div>
                  <h4>Delivery</h4>
                  <pre>{stringify({
                    id: row.id,
                    dedupeKey: row.dedupeKey,
                    scheduledFor: row.scheduledFor,
                    expiresAt: row.expiresAt,
                    lastAttemptAt: row.lastAttemptAt,
                    fcmMessageId: row.fcmMessageId,
                    fcmErrorCode: row.fcmErrorCode,
                    fcmErrorMessage: row.fcmErrorMessage,
                    fcmToken: row.fcmToken,
                  })}</pre>
                </div>
              </div>
            ),
          }}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: [25, 50, 100, 200],
            onChange: (page, pageSize) => loadData(page, pageSize),
          }}
          rowKey="id"
          scroll={{ x: 1700 }}
          size="middle"
        />
      </div>
    </Body>
  );
};

export default NotificationOutbox;
